
import { useEffect, Suspense, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import PremiumHeroSection from '@/components/PremiumHeroSection';
import TrustedClientsSection from '@/components/professionnels/TrustedClientsSection';
import Footer from '@/components/Footer';

import { StructuredData } from '@/components/StructuredData';
import { SmartLazySection } from '@/components/ui/smart-lazy-section';
import { SeoRichContent } from '@/components/seo/SeoRichContent';
import { InternalLinksSection } from '@/components/seo/InternalLinksSection';
import { EnhancedStructuredData } from '@/components/seo/EnhancedStructuredData';
import { 
  LazyVosInspirations,
  LazyPromiseSection,
  LazyProfessionalSection,
  LazyNewsletterSection,
  LazyGoogleReviewSection,
  LazyEventsSection,
  LazyEventTypeSection
} from '@/components/LazyComponents';
import { lazy } from 'react';
import { useRealtimeSettings } from '@/hooks/useRealtimeSettings';
const LazyLieuxPartenaires = lazy(() => import('@/components/lieux/LieuxPartenairesSection'));
import { useDynamicSeo } from '@/hooks/seo/useDynamicSeo';
import { usePreloadCriticalData } from '@/hooks/useOptimizedDatabase';
import { defaultSettings } from '@/types/settings';
import { useIsMobile } from '@/hooks/use-mobile';
import { AnimatedSection } from '@/components/AnimatedSection';
import TerroirsSection from '@/components/TerroirsSection';
import { imageSociale } from '@/utils/imageSociale';
import { ADRESSE_ENTREPRISE, PROFILS_ENTREPRISE } from '@/constants/entreprise';

const Index = memo(() => {
  const location = useLocation();
  const { settings } = useRealtimeSettings();
  const isMobile = useIsMobile();
  
  // Préchargement des données critiques
  const { preloadData } = usePreloadCriticalData();

  // Préchargement des données critiques (une seule fois au montage).
  // L'ancien setInterval(optimizeCache, 120000) a été retiré : React Query gère
  // déjà l'éviction du cache via gcTime, et l'intervalle provoquait des
  // re-rendus + refetch périodiques inutiles sur la page d'accueil publique.
  useEffect(() => {
    preloadData();
  }, []);

  // NB : useRealtimeUpdates() (notifications admin temps réel — toasts
  // « Nouvelle demande »/« Nouvel évènement ») a été retiré de l'accueil public :
  // il ouvrait 2 WebSockets Supabase sur chaque visite et n'a de sens que dans
  // l'espace admin. À monter côté layout admin si besoin.

  // Activer le SEO dynamique et récupérer les métadonnées
  const { metadata } = useDynamicSeo();

  // Afficher une notification si un message est présent dans l'état de localisation
  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message);
      // Effacer le message après l'avoir affiché
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Utiliser les paramètres ou les valeurs par défaut
  const currentSettings = settings || defaultSettings;

  // Utiliser les métadonnées dynamiques ou les valeurs par défaut
  const pageTitle = metadata?.title || 'Maison Eridia — Organisation d\'évènements privés en Île-de-France';
  const pageDescription = metadata?.meta_description || 'Maison Eridia organise vos évènements d\'exception : wedding planning, anniversaires signature, demandes en mariage. Scénographie, coordination, prestations premium en Île-de-France.';
  const pageKeywords = metadata?.keywords || 'organisation évènements, mariage sur-mesure Paris, anniversaire signature, demande en mariage, wedding planning, coordination évènement, scénographie, Maison Eridia';

  return (
    <div className="min-h-screen bg-background font-sans" style={{ fontSize: isMobile ? '16px' : '14px' }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href="https://maisoneridia.fr/" />
        
        {/* Critical resource preloading with responsive images */}
        <link 
          rel="preload" 
          href="/lovable-uploads/7b97ec36-7bc1-4ec7-9b50-adb7b96660cf.png?width=1400&quality=70&format=webp&resize=cover" 
          as="image" 
          fetchPriority="high"
          imageSrcSet={`
            /lovable-uploads/7b97ec36-7bc1-4ec7-9b50-adb7b96660cf.png?width=480&quality=60&format=webp&resize=cover 480w,
            /lovable-uploads/7b97ec36-7bc1-4ec7-9b50-adb7b96660cf.png?width=768&quality=65&format=webp&resize=cover 768w,
            /lovable-uploads/7b97ec36-7bc1-4ec7-9b50-adb7b96660cf.png?width=1200&quality=70&format=webp&resize=cover 1200w,
            /lovable-uploads/7b97ec36-7bc1-4ec7-9b50-adb7b96660cf.png?width=1920&quality=70&format=webp&resize=cover 1920w
          `}
          imageSizes="(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1200px) 1200px, 1920px"
        />
        
        {/* Performance optimizations */}
        <link rel="dns-prefetch" href="//fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {isMobile && (
          <>
            <meta name="format-detection" content="telephone=no" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=yes, maximum-scale=5.0" />
          </>
        )}
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content="https://maisoneridia.fr/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={imageSociale('/lovable-uploads/88d5cb53-aa4f-4945-9ef1-e525b827face.png')} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={imageSociale('/lovable-uploads/88d5cb53-aa4f-4945-9ef1-e525b827face.png')} />
        
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Maison Eridia",
              "url": "https://maisoneridia.fr",
              "logo": "/lovable-uploads/maison-eridia-logo.png",
              "description": "Organisation complète d'évènements privés élégants en Île-de-France",
              "image": "/lovable-uploads/maison-eridia-logo.png",
              "telephone": "${currentSettings.phone || '+33 7 49 40 34 60'}",
              "email": "${currentSettings.email || 'hello@maisoneridia.com'}",
              "areaServed": {
                "@type": "State",
                "name": "Île-de-France"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "${currentSettings.phone || '+33 7 49 40 34 60'}",
                "contactType": "customer service",
                "email": "${currentSettings.email || 'hello@maisoneridia.com'}",
                "areaServed": "Île-de-France"
              },
              "address": ${JSON.stringify(ADRESSE_ENTREPRISE)},
              "priceRange": "€€€",
              "knowsAbout": ["organisation évènements", "mariage sur-mesure Paris", "anniversaire signature Île-de-France", "wedding planning Paris", "wedding design scénographie", "coordination évènementielles Île-de-France", "demande en mariage"],
              "sameAs": ${JSON.stringify(PROFILS_ENTREPRISE)}
            }
          `}
        </script>
      </Helmet>
      
      <StructuredData />
      <EnhancedStructuredData type="home" />
      
      <Navbar />
      <PremiumHeroSection />
      
      {/* Section partenaires - juste après le hero */}
      <TrustedClientsSection />

      {/* Section avec les cartes évènements privés/pro */}
      <SmartLazySection height="400px" rootMargin="200px">
        <Suspense fallback={<div className="h-[400px]" />}>
          <AnimatedSection animation="fade-up" delay={0.2}>
            <LazyEventTypeSection />
          </AnimatedSection>
        </Suspense>
      </SmartLazySection>

      {/* Avis Google — preuve sociale authentique */}
      <SmartLazySection height="h-96" threshold={0.2} rootMargin="100px">
        <AnimatedSection animation="fade-up">
          <LazyGoogleReviewSection />
        </AnimatedSection>
      </SmartLazySection>

      {/* Notre promesse — le "pourquoi nous" avant les galeries */}
      <SmartLazySection height="h-64" threshold={0.2} rootMargin="150px">
        <AnimatedSection animation="scale">
          <LazyPromiseSection />
        </AnimatedSection>
      </SmartLazySection>

      {/* Section évènements mise en avant - chargement optimisé */}
      <SmartLazySection height="600px" rootMargin="200px">
        <Suspense fallback={<div className="h-[600px]" />}>
          <AnimatedSection animation="fade-up" delay={0.1}>
            <LazyEventsSection />
          </AnimatedSection>
        </Suspense>
      </SmartLazySection>

      {/* Galerie inspiration */}
      <SmartLazySection height="h-96" threshold={0.2} rootMargin="200px">
        <AnimatedSection animation="fade-up">
          <LazyVosInspirations />
        </AnimatedSection>
      </SmartLazySection>

      {/* Section lieux partenaires */}
      <SmartLazySection height="h-96" threshold={0.15} rootMargin="150px">
        <AnimatedSection animation="fade-up">
          <Suspense fallback={<div className="h-[400px]" />}>
            <LazyLieuxPartenaires />
          </Suspense>
        </AnimatedSection>
      </SmartLazySection>

      {/* Section dédiée aux professionnels */}
      <SmartLazySection height="h-96" threshold={0.2} rootMargin="150px">
        <AnimatedSection animation="fade-up">
          <LazyProfessionalSection />
        </AnimatedSection>
      </SmartLazySection>

      <SmartLazySection height="h-48" threshold={0.2} rootMargin="100px">
        <AnimatedSection animation="fade-in">
          <LazyNewsletterSection />
        </AnimatedSection>
      </SmartLazySection>
      
      {/* Contenu SEO riche */}
      <SeoRichContent page="home" />

      {/* Nos terroirs d'intervention + tous nos formats */}
      <TerroirsSection />

      {/* Maillage interne */}
      <InternalLinksSection currentPage="/" />
      
      <Footer />
      
    </div>
  );
});

Index.displayName = 'Index';

export default Index;
